import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Check, ArrowRight, ArrowLeft, Loader2, Sparkles, Save, Send, X,
  ClipboardList, FileSearch, FileSignature, CreditCard, Rocket,
} from 'lucide-react';
import { clientApi } from '@/lib/clientApi';

const money = (n) => (n == null ? null : `$${Number(n).toLocaleString()}`);

/* ── Shared requirements questionnaire (plain language for founders) ── */
export const RequirementsForm = ({ questions, initial, onSaveDraft, onSubmit, saving, submitting }) => {
  const [answers, setAnswers] = useState(initial || {});
  useEffect(() => { setAnswers(initial || {}); }, [initial]);
  const set = (id) => (e) => setAnswers((a) => ({ ...a, [id]: e.target.value }));
  const answeredCount = questions.filter((q) => (answers[q.id] || '').trim()).length;

  return (
    <div data-testid="requirements-form">
      <div className="space-y-5">
        {questions.map((q, i) => (
          <div key={q.id}>
            <label className="block text-sm font-semibold text-slate-800 mb-1.5">
              <span className="text-brand-blue mr-1.5">{i + 1}.</span>{q.label}
            </label>
            <textarea
              rows={2}
              value={answers[q.id] || ''}
              onChange={set(q.id)}
              placeholder={q.placeholder}
              className="w-full px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/25 resize-none"
              data-testid={`req-input-${q.id}`}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-3 mt-6">
        <button
          onClick={() => onSaveDraft(answers)}
          disabled={saving || submitting}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[color:var(--hairline)] text-sm font-semibold text-slate-700 hover:bg-secondary/60 transition-colors disabled:opacity-50"
          data-testid="req-save-draft"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save &amp; finish later
        </button>
        <button
          onClick={() => onSubmit(answers)}
          disabled={submitting || saving || answeredCount === 0}
          className="flex-1 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold shadow-md shadow-brand-blue/15 hover:bg-brand-blue-dark transition-colors disabled:opacity-50"
          data-testid="req-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit requirements
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{answeredCount} of {questions.length} answered · you can save and come back anytime.</p>
    </div>
  );
};

const NEXT_STEPS = [
  { icon: ClipboardList, title: 'You share requirements', desc: 'Tell us what you need in plain language.' },
  { icon: FileSearch, title: 'We scope it', desc: 'Our team prepares a clear scope for your approval.' },
  { icon: FileSignature, title: 'You approve & sign', desc: 'Review the scope and accept a simple agreement.' },
  { icon: CreditCard, title: 'First payment', desc: 'Secure checkout (activating soon) before kickoff.' },
  { icon: Rocket, title: 'We build & manage', desc: 'A first version can launch in as little as 15 business days.' },
];

/* ── Onboarding wizard: Select Plan -> Create -> Requirements -> What's next ── */
export const OnboardingWizard = ({ plans, existingProject, initialPlanId, initialCycle, onDone, onClose }) => {
  const resumingDraft = !!existingProject;
  const [step, setStep] = useState(resumingDraft ? 2 : 0);
  const [planId, setPlanId] = useState(existingProject?.plan_id || initialPlanId || '');
  const [cycle, setCycle] = useState((existingProject?.billing_cycle || initialCycle) === 'annual' ? 'annual' : 'monthly');
  const [name, setName] = useState(existingProject?.name || '');
  const [project, setProject] = useState(existingProject || null);
  const [questions, setQuestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    clientApi.onboardingQuestions().then((d) => setQuestions(d.questions || [])).catch(() => {});
  }, []);

  const steps = ['Choose a plan', 'Name your project', 'Tell us what you need', 'All set'];

  const createProject = async () => {
    if (!name.trim()) { toast.error('Please give your project a name.'); return; }
    setBusy(true);
    try {
      const d = await clientApi.createProject({ name: name.trim(), plan_id: planId, billing_cycle: cycle });
      setProject(d.project);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the project. Please try again.');
    } finally { setBusy(false); }
  };

  const saveDraft = async (answers) => {
    if (!project) return;
    setSaving(true);
    try { await clientApi.saveRequirements(project.id, answers); toast.success('Saved. You can finish this anytime.'); onDone?.({ stay: false }); }
    catch (e) { toast.error('Could not save right now.'); }
    finally { setSaving(false); }
  };

  const submit = async (answers) => {
    if (!project) return;
    setSubmitting(true);
    try { await clientApi.submitRequirements(project.id, answers); setStep(3); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit requirements.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-0 sm:p-6 bg-slate-900/40 backdrop-blur-sm overflow-y-auto" data-testid="onboarding-wizard">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative bg-background w-full sm:max-w-2xl sm:rounded-2xl shadow-[var(--shadow-lift)] border border-[color:var(--hairline)] min-h-screen sm:min-h-0 my-0 sm:my-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--hairline)] sticky top-0 bg-background/95 backdrop-blur z-10 sm:rounded-t-2xl">
          <div>
            <p className="text-[11px] font-semibold text-brand-blue uppercase tracking-wider">New project onboarding</p>
            <h2 className="text-lg font-bold text-slate-900">{steps[step]}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary/60 text-slate-500" data-testid="onboarding-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1.5 px-6 pt-4">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-brand-blue' : 'bg-secondary'}`} />
          ))}
        </div>

        <div className="p-6">
          {/* Step 0 — plan */}
          {step === 0 && (
            <div data-testid="onboarding-step-plan">
              <p className="text-sm text-muted-foreground mb-4">Pick the plan that fits where you are today. You can change this with our team before anything is billed.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {plans.map((p) => {
                  const selected = planId === p.id;
                  const price = p.quote_only ? 'Custom quote' : `${money(cycle === 'annual' ? p.annual_usd : p.monthly_usd)}${cycle === 'annual' ? '/yr' : '/mo'}`;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setPlanId(p.id)}
                      className={`text-left rounded-2xl border p-4 transition-all ${selected ? 'border-brand-blue ring-2 ring-brand-blue/20 bg-brand-blue/[0.03]' : 'border-[color:var(--hairline)] hover:border-brand-blue/40'}`}
                      data-testid={`onboarding-plan-${p.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-900">{p.name}</span>
                        {selected && <Check className="w-4 h-4 text-brand-blue" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 min-h-[32px]">{p.tagline}</p>
                      <p className="mt-2 text-base font-extrabold text-brand-orange">{price}</p>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-3 mt-4">
                <span className={`text-xs font-semibold ${cycle === 'monthly' ? 'text-slate-900' : 'text-muted-foreground'}`}>Monthly</span>
                <button onClick={() => setCycle((c) => (c === 'monthly' ? 'annual' : 'monthly'))} className={`relative w-11 h-6 rounded-full transition-colors ${cycle === 'annual' ? 'bg-brand-blue' : 'bg-slate-300'}`} data-testid="onboarding-cycle-toggle">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cycle === 'annual' ? 'translate-x-5' : ''}`} />
                </button>
                <span className={`text-xs font-semibold ${cycle === 'annual' ? 'text-slate-900' : 'text-muted-foreground'}`}>Annual</span>
              </div>
              <div className="flex justify-end mt-6">
                <button onClick={() => planId && setStep(1)} disabled={!planId} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors disabled:opacity-40" data-testid="onboarding-next-plan">Continue <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {/* Step 1 — name + create */}
          {step === 1 && (
            <div data-testid="onboarding-step-name">
              <label className="block text-sm font-semibold text-slate-800 mb-1.5">What should we call this project?</label>
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Clinic Booking App"
                className="w-full px-3.5 py-2.5 rounded-xl border border-[color:var(--hairline)] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/25"
                data-testid="onboarding-project-name"
              />
              <div className="flex items-center justify-between mt-6">
                <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[color:var(--hairline)] text-sm font-semibold text-slate-700 hover:bg-secondary/60 transition-colors"><ArrowLeft className="w-4 h-4" /> Back</button>
                <button onClick={createProject} disabled={busy || !name.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors disabled:opacity-40" data-testid="onboarding-create-project">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Create project
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — requirements */}
          {step === 2 && (
            <div data-testid="onboarding-step-requirements">
              <p className="text-sm text-muted-foreground mb-4">No tech knowledge needed — answer in your own words. Your answers are saved as you go.</p>
              <RequirementsForm
                questions={questions}
                initial={project?.requirements || {}}
                onSaveDraft={saveDraft}
                onSubmit={submit}
                saving={saving}
                submitting={submitting}
              />
            </div>
          )}

          {/* Step 3 — confirmation */}
          {step === 3 && (
            <div data-testid="onboarding-step-done" className="text-center">
              <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Requirements received</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Our team will review and prepare your scope. Here&apos;s what happens next:</p>
              <div className="space-y-2.5 text-left max-w-md mx-auto mb-6">
                {NEXT_STEPS.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 rounded-xl border border-[color:var(--hairline)] p-3 bg-white">
                      <div className="w-8 h-8 rounded-lg bg-brand-blue/10 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-brand-blue" /></div>
                      <div><p className="text-sm font-semibold text-slate-900">{s.title}</p><p className="text-xs text-muted-foreground">{s.desc}</p></div>
                    </div>
                  );
                })}
              </div>
              <button onClick={() => onDone?.({ stay: true, projectId: project?.id })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors" data-testid="onboarding-go-workspace">Go to my project <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
